import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Heart, Bookmark, Share2, MessageCircle, Send, Volume2, VolumeX, Play,
    BadgeCheck, MapPin, Home as HomeIcon, Flag, Phone, ChevronRight
} from "lucide-react";
import { api, fileUrl, formatINR } from "../../lib/api";
import { registerReelVideo, unregisterReelVideo } from "./reelsController";

/** Full-screen vertical video player with property meta + CTA rail. */
export default function ReelPlayer({
    reel, active, muted,
    onToggleMute, onLike, onSave, onShare, onEnquire, onComment, onReport,
}) {
    const vidRef = useRef(null);
    const [paused, setPaused] = useState(false);

    // Register this video element with the central playback controller.
    // The controller — driven by ReelsFeed — decides who plays.
    useEffect(() => {
        const v = vidRef.current;
        if (!v) return;
        registerReelVideo(reel.id, v);
        return () => { unregisterReelVideo(reel.id); };
    }, [reel.id]);

    // Play/pause hint for the "paused" overlay only — actual play/pause is centralized.
    useEffect(() => {
        const v = vidRef.current;
        if (!v) return;
        const onPause = () => setPaused(true);
        const onPlay  = () => setPaused(false);
        v.addEventListener("pause", onPause);
        v.addEventListener("play",  onPlay);
        return () => {
            v.removeEventListener("pause", onPause);
            v.removeEventListener("play",  onPlay);
        };
    }, []);

    // Global first-interaction listener: after any tap/touch, try to unmute active video
    useEffect(() => {
        const unlock = () => {
            const v = vidRef.current;
            if (v && !muted) { v.muted = false; v.play().catch(() => {}); }
            window.removeEventListener("touchstart", unlock);
            window.removeEventListener("click", unlock);
        };
        window.addEventListener("touchstart", unlock, { once: true, passive: true });
        window.addEventListener("click", unlock, { once: true });
        return () => {
            window.removeEventListener("touchstart", unlock);
            window.removeEventListener("click", unlock);
        };
    }, [muted]);

    const p = reel.property || {};
    const c = reel.creator || {};
    const ownerPhone = c.phone || p.contact_phone || null;

    const trackContact = (channel) => {
        api.post(`/reels/${reel.id}/contact-click`, { channel }).catch(() => {});
    };

    const onCall = () => {
        if (!ownerPhone) return;
        trackContact("call");
        window.location.href = `tel:${ownerPhone}`;
    };

    const onWhatsApp = () => {
        if (!ownerPhone) return;
        trackContact("whatsapp");
        const msg = `Hi, I'm interested in "${p.title || "your property"}" I saw on Global Property Mart Reels.`;
        const num = String(ownerPhone).replace(/\D/g, "");
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    };

    const togglePlay = () => {
        const v = vidRef.current;
        if (!v) return;
        if (v.paused) { v.play(); setPaused(false); }
        else { v.pause(); setPaused(true); }
    };

    const priceLabel = p.price != null ? formatINR(p.price) : null;
    const typeLabel  = [p.bhk ? `${p.bhk} BHK` : null, p.subtype || p.property_type].filter(Boolean).join(" · ");
    const locLabel   = [p.locality, p.city].filter(Boolean).join(", ");

    return (
        <div className="relative h-full w-full bg-black overflow-hidden" data-testid={`reel-slide-${reel.id}`}>
            {/* Video */}
            <video
                ref={vidRef}
                src={fileUrl(reel.video_url)}
                poster={reel.thumbnail_url ? fileUrl(reel.thumbnail_url) : undefined}
                muted={muted}
                loop
                playsInline
                preload="metadata"
                onClick={togglePlay}
                className="absolute inset-0 w-full h-full object-contain md:object-cover bg-black"
                data-testid={`reel-video-${reel.id}`}
            />

            {/* Gradient overlays */}
            <div className="absolute inset-x-0 top-0 h-32 pointer-events-none"
                 style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55), transparent)" }} />
            <div className="absolute inset-x-0 bottom-0 h-56 pointer-events-none"
                 style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.72))" }} />

            {/* Play/pause overlay */}
            {paused && (
                <button
                    aria-label="Play"
                    onClick={togglePlay}
                    className="absolute inset-0 flex items-center justify-center"
                    data-testid={`reel-play-${reel.id}`}
                >
                    <div className="w-16 h-16 rounded-full flex items-center justify-center"
                         style={{ background: "rgba(0,0,0,0.5)" }}>
                        <Play className="w-8 h-8 text-white" />
                    </div>
                </button>
            )}

            {/* Mute toggle — small, top-right, no hint UI */}
            <button
                aria-label={muted ? "Unmute" : "Mute"}
                onClick={onToggleMute}
                className="absolute top-16 right-3 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "rgba(0,0,0,0.5)" }}
                data-testid={`reel-mute-${reel.id}`}
            >
                {muted ? <VolumeX className="w-3.5 h-3.5 text-white" /> : <Volume2 className="w-3.5 h-3.5 text-white" />}
            </button>

            {/* Right rail — compact, centered vertically */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2 z-10" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>
                <RailButton onClick={onEnquire} testid={`reel-enquire-icon-${reel.id}`}
                    icon={<Send className="w-4 h-4 text-white" />}
                    label="Enquire" />
                {ownerPhone && (
                    <>
                        <RailButton onClick={onCall} testid={`reel-call-${reel.id}`}
                            icon={<Phone className="w-4 h-4 text-white" />}
                            label="Call" />
                        <RailButton onClick={onWhatsApp} testid={`reel-whatsapp-${reel.id}`}
                            icon={
                                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#25D366" aria-hidden="true">
                                    <path d="M12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 2.09.66 4.03 1.79 5.62L2 22l4.62-1.87c1.53.83 3.28 1.31 5.11 1.31h.31c5.46 0 9.9-4.44 9.9-9.9 0-2.65-1.03-5.14-2.9-7.02C17.16 2.65 14.67 2 12.04 2zm5.5 14.02c-.23.66-1.36 1.26-1.9 1.34-.49.07-1.09.1-1.75-.11-.4-.13-.92-.3-1.58-.58-2.79-1.2-4.6-4.02-4.75-4.21-.14-.19-1.14-1.52-1.14-2.9 0-1.38.72-2.06.98-2.34.26-.29.57-.36.76-.36.19 0 .38 0 .55.01.18.01.42-.07.65.5.24.58.83 2 .9 2.14.07.14.11.31.02.5-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.17.29.75 1.24 1.61 2.01 1.11.99 2.04 1.29 2.33 1.44.29.14.46.12.63-.07.17-.19.72-.84.91-1.13.19-.29.38-.24.65-.14.26.09 1.68.79 1.96.94.29.14.48.21.55.33.07.13.07.75-.16 1.4z" />
                                </svg>
                            }
                            label="WA" />
                    </>
                )}
                <RailButton onClick={onComment} testid={`reel-comment-${reel.id}`}
                    icon={<MessageCircle className="w-4 h-4 text-white" />}
                    label={reel.comments_count || 0} />
                <RailButton onClick={onLike} testid={`reel-like-${reel.id}`}
                    icon={<Heart className={`w-4 h-4 ${reel.liked ? "fill-red-500 text-red-500" : "text-white"}`} />}
                    label={reel.likes_count || 0} />
                <RailButton onClick={onShare} testid={`reel-share-${reel.id}`}
                    icon={<Share2 className="w-4 h-4 text-white" />}
                    label={reel.shares_count || 0} />
                <RailButton onClick={onSave} testid={`reel-save-${reel.id}`}
                    icon={<Bookmark className={`w-4 h-4 ${reel.saved ? "fill-white text-white" : "text-white"}`} />} />
                <RailButton onClick={onReport} testid={`reel-report-${reel.id}`}
                    icon={<Flag className="w-3.5 h-3.5 text-white/85" />} />
            </div>

            {/* Bottom info + CTAs — all text WHITE with text-shadow for readability over videos */}
            <div className="absolute inset-x-0 bottom-0 p-4 pb-6 z-10 pr-16" data-testid={`reel-info-${reel.id}`}
                 style={{ color: "#FFFFFF", textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}>
                {/* Creator strip */}
                <Link
                    to={c.id ? `/partners/${c.id}` : "#"}
                    className="flex items-center gap-2 mb-2"
                    data-testid={`reel-creator-${reel.id}`}
                >
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-white/20 flex items-center justify-center text-[11px] font-bold shrink-0"
                         style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)", color: "#001a3d" }}>
                        {c.avatar_url
                            ? <img alt="" src={fileUrl(c.avatar_url)} className="w-full h-full object-cover" />
                            : (c.name || "G").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="text-[12px] leading-tight min-w-0" style={{ color: "#FFFFFF" }}>
                        <div className="flex items-center gap-1">
                            <span className="font-extrabold truncate" style={{ color: "#FFFFFF" }}>{c.name || "GPM Partner"}</span>
                            {(c.verified || p.verified) && <BadgeCheck className="w-3.5 h-3.5" style={{ color: "#F5E5A5" }} />}
                        </div>
                        <div className="text-[10.5px] truncate" style={{ color: "rgba(255,255,255,0.85)" }}>
                            {(c.role || "").replace(/_/g, " ") || "Partner"}{c.city ? ` · ${c.city}` : ""}
                        </div>
                    </div>
                </Link>

                {/* Property title / price / location — all white */}
                <div>
                    <div className="font-heading font-extrabold text-[15px] leading-snug line-clamp-2" data-testid={`reel-title-${reel.id}`} style={{ color: "#FFFFFF" }}>
                        {p.title || reel.caption || "GPM Reel"}
                    </div>
                    {(priceLabel || typeLabel) && (
                        <div className="mt-1 flex items-center gap-2 flex-wrap text-[11.5px]">
                            {priceLabel && (
                                <span className="px-2 py-0.5 rounded-full font-extrabold"
                                      style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d", textShadow: "none" }}
                                      data-testid={`reel-price-${reel.id}`}>
                                    {priceLabel}
                                </span>
                            )}
                            {typeLabel && <span style={{ color: "#FFFFFF" }}>{typeLabel}</span>}
                        </div>
                    )}
                    {locLabel && (
                        <div className="mt-1 flex items-center gap-1 text-[11px]" style={{ color: "#FFFFFF" }}>
                            <MapPin className="w-3 h-3" /> {locLabel}
                        </div>
                    )}
                    {reel.caption && p.title && (
                        <div className="mt-2 text-[11.5px] line-clamp-2" style={{ color: "#FFFFFF" }}>{reel.caption}</div>
                    )}
                </div>

                {/* CTA row — only when a property is linked */}
                {p.id && (
                    <div className="mt-3 flex items-center gap-2">
                        <Link
                            to={`/properties/${p.id}`}
                            data-testid={`reel-view-property-${reel.id}`}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl text-[12px] font-extrabold shadow"
                            style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d", textShadow: "none" }}
                        >
                            <HomeIcon className="w-3.5 h-3.5" /> View Property <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                        <button
                            onClick={onEnquire}
                            data-testid={`reel-enquire-${reel.id}`}
                            className="px-3 py-2.5 rounded-xl text-[12px] font-extrabold border"
                            style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.4)", color: "#FFFFFF" }}
                        >
                            Enquire
                        </button>
                    </div>
                )}
            </div>

        </div>
    );
}

function RailButton({ icon, label, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-0.5 select-none">
            <div className="w-8 h-8 rounded-full flex items-center justify-center active:scale-95 transition-transform"
                 style={{ background: "rgba(0,0,0,0.55)" }}>
                {icon}
            </div>
            {label != null && (
                <span className="text-[9px] font-bold leading-none"
                      style={{ color: "#FFFFFF", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
                    {label}
                </span>
            )}
        </button>
    );
}
